// UX43 (docs/build-briefs/UX43-magnitude-lists-sorted.md) Section 3.4.
// Dependency-free: no `window` or `document` access, the same
// established shape harness/src/domain/settingsBounds.ts and
// harness/scripts/floorSurfaces.ts already use, so a plain `node:test`
// file can import bySizeTokenAmount directly with no DOM shim. This
// module was split out of ui/src/admin.ts specifically for that reason:
// admin.ts itself is NOT DOM-free at import (396 module-level
// `mustFind(...)` calls resolve real DOM elements as soon as the module
// loads), so importing admin.ts directly from a plain node:test file
// throws "document is not defined" before any test body runs, confirmed
// this session (`node --experimental-strip-types -e
// 'import("./ui/src/admin.ts")'` -> "document is not defined"). The
// brief's own Section 4.1 assumed admin.ts itself was DOM-free at import,
// the same way ui/src/session.ts is (test/ui-has-permission.test.ts's own
// precedent); that assumption does not hold for admin.ts, so the pure
// comparator and its parser move here instead. admin.ts still owns every
// display-only concern (SIZE_UNIT_DISPLAY, fallbackSizeTokenLabel) and
// imports parseSizeToken/bySizeTokenAmount from this module rather than
// defining them inline.

// B4 (docs/backlog.md, owner ruling 2026-08-27): the bottle "Size" field.
// A vocabulary VALUE here is its own amount and unit run together with no
// separator, e.g. "750ml" or "1.75l" or "12oz" (assertValidVocabularyValue
// only allows lower-case letters, digits, underscore and dot, so this is
// the one encoding that fits without a schema change). SIZE_TOKEN_PATTERN
// parses one back into the same (value, unit) shape the form's own
// underlying inputs (#admin-bottle-size-value / -size-unit) already store,
// so a selection round-trips into the EXISTING sizeValue/sizeUnit columns
// with no new field and no migration.
const SIZE_TOKEN_PATTERN = /^(\d+(?:\.\d+)?)(ml|l|oz)$/;

export function parseSizeToken(token: string): { value: string; unit: string } | undefined {
  const match = SIZE_TOKEN_PATTERN.exec(token);
  if (match === null) {
    return undefined;
  }
  const value = match[1];
  const unit = match[2];
  if (value === undefined || unit === undefined) {
    return undefined;
  }
  return { value, unit };
}

/** UX43 Section 3.4: ml/l converted to a shared millilitre base before
 * comparing, so "1l" (1000) correctly sorts after "750ml" (750) rather
 * than comparing "1" against "750" as bare numbers. oz is its own
 * unconverted family (serving_size never carries ml or l,
 * UNITS_BY_VOCABULARY in src/domain/vocabularies.ts) and needs no
 * conversion. A token parseSizeToken cannot read sorts to the end, never
 * thrown, so a row is never dropped from the rendered list, only
 * reordered; Array.prototype.sort's stability keeps two unparseable
 * tokens in their original relative order. */
const SIZE_UNIT_MULTIPLIER: Readonly<Record<string, number>> = { ml: 1, l: 1000, oz: 1 };

function sizeTokenSortKey(token: string): number {
  const parsed = parseSizeToken(token);
  if (parsed === undefined) {
    return Number.POSITIVE_INFINITY;
  }
  const amount = Number(parsed.value);
  if (!Number.isFinite(amount)) {
    return Number.POSITIVE_INFINITY;
  }
  return amount * (SIZE_UNIT_MULTIPLIER[parsed.unit] ?? 1);
}

export function bySizeTokenAmount<T extends { value: string }>(entries: readonly T[]): T[] {
  return [...entries].sort((a, b) => sizeTokenSortKey(a.value) - sizeTokenSortKey(b.value));
}
